import clsx from "clsx";
import { format } from "date-fns";
import { FC, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";
import { bigintToFixed, MsgExecute, priceFormatter } from "rujira.js";
import {
  Button,
  Decimal,
  i18n,
  IconDenom,
  nFormatter,
  useGlobalModalContext,
  useIsTouchDevice,
} from "rujira.ui";
import {
  CirclePen,
  CirclesXMark,
  CircleXMark,
  Claim,
  ClaimAll,
  ClaimSolid,
} from "rujira.ui/src/components/icons/Icons";
import {
  BalanceProvider,
  usePreloadedBalance,
} from "../../common/components/Balance";
import { MsgProvider, TxButton } from "../../common/components/TxButton";
import { useAccounts } from "../../services/accounts";
import { toFinAssetId } from "../Trade";
import { useTradeBookSubscription } from "../TradeSubscriptions";
import { InputQuantity } from "./InputQuantity";
import { OrderNode } from "./Orders";
import {
  LimitOrdersFragment$data,
  LimitOrdersFragment$key,
} from "./__generated__/LimitOrdersFragment.graphql";

type FinOrderNode = Extract<OrderNode, { __typename: "FinOrder" }>;

type LimitOrdersTableProps = FC<{
  orders: NonNullable<FinOrderNode>[];
  contract?: string;
  label: string;
  expanded?: boolean;
}>;

export const LimitOrdersTable: LimitOrdersTableProps = ({
  orders,
  contract,
  label,
  expanded,
}) => {
  const { selected } = useAccounts();
  const isTouchDevice = useIsTouchDevice();

  const claimAll = useMemo(() => {
    if (!selected) return null;
    if (!contract) return null;

    const toClaim = orders.filter(
      (o) => o.filled && o.pair.address === contract
    );

    if (!toClaim.length) return null;
    return new MsgExecute(
      { address: selected.address, network: "THOR" },
      [],
      contract,
      {
        order: [orders.map((x) => [x.side, priceKey(x), null]), null],
      }
    );
  }, [orders, contract]);

  const cancelAll = useMemo(() => {
    if (!contract) return null;
    if (!selected) return null;
    const toCancel = orders.filter(
      (o) => o.remaining && o.pair.address === contract
    );
    if (!toCancel.length) return null;
    return new MsgExecute(
      { address: selected.address, network: "THOR" },
      [],
      contract,
      {
        order: [orders.map((x) => [x.side, priceKey(x), "0"]), null],
      }
    );
  }, [orders, contract]);

  return (
    <div className="trade__orders-table table-sticky table-sticky--left table-sticky--right mt-2">
      {orders.length > 0 ? (
        <table
          className={clsx({
            "table nowrap table--spaced": true,
            "table--no-hover": isTouchDevice,
          })}>
          <thead>
            <tr>
              <i18n.th className="w-1">Pair</i18n.th>
              <th></th>
              <i18n.th className="text-center">Side</i18n.th>
              <i18n.th className="text-center">Type</i18n.th>
              <i18n.th>Date</i18n.th>
              <i18n.th className="text-right">Current Price</i18n.th>
              <i18n.th className="text-right">Order Price</i18n.th>
              <i18n.th className="text-right">Amount</i18n.th>
              <i18n.th className="text-right">Remaining</i18n.th>
              <i18n.th className="text-right">Value</i18n.th>
              <i18n.th className="text-right">Filled</i18n.th>
              <i18n.th className="text-right">Unclaimed</i18n.th>
              <i18n.th className="text-right">Fee</i18n.th>
              {contract ? (
                <th className="w-6">
                  <div className="flex jc-e w-full">
                    <MsgProvider msg={cancelAll}>
                      <TxButton
                        hideSimulation
                        className={clsx({
                          "block transparent w-3 h-3 color-grey": true,
                          "opacity-10": false,
                          "hover-white": true,
                        })}
                        data-tooltip-content="Cancel all"
                        data-tooltip-id="global-tip"
                        data-tooltip-float={false}>
                        <CirclesXMark className="block w-3 h-3" />
                      </TxButton>
                    </MsgProvider>
                    <MsgProvider msg={claimAll}>
                      <TxButton
                        hideSimulation
                        className={clsx({
                          "block transparent w-3 h-3 color-grey ml-0.5": true,
                          "opacity-10": false,
                          "hover-white": true,
                        })}
                        data-tooltip-content="Claim all"
                        data-tooltip-id="global-tip"
                        data-tooltip-float={false}>
                        <ClaimAll className="block w-3 h-3" />
                      </TxButton>
                    </MsgProvider>
                  </div>
                </th>
              ) : (
                <th className="nowrap w-6"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {orders.map((o, idx) => (
              <LimitOrderItem
                key={idx}
                k={o}
                iconSize={expanded ? "medium" : "small"}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="condensed fs-14 color-grey text-center mt-2">
          You have no {label} orders
        </p>
      )}
    </div>
  );
};

export const LimitOrdersTableFallback: FC = () => {
  const isTouchDevice = useIsTouchDevice();

  return (
    <div className="trade__orders-table table-sticky table-sticky--left mt-2">
      <table
        className={clsx({
          "table nowrap table--spaced": true,
          "table--no-hover": isTouchDevice,
        })}>
        <thead>
          <tr>
            <i18n.th className="w-1">Pair</i18n.th>
            <th></th>
            <i18n.th className="text-center">Side</i18n.th>
            <i18n.th className="text-center">Type</i18n.th>
            <i18n.th>Date</i18n.th>
            <i18n.th className="text-right">Current Price</i18n.th>
            <i18n.th className="text-right">Order Price</i18n.th>
            <i18n.th className="text-right">Amount</i18n.th>
            <i18n.th className="text-right">Remaining</i18n.th>
            <i18n.th className="text-right">Value</i18n.th>
            <i18n.th className="text-right">Filled</i18n.th>
            <i18n.th className="text-right">Fee</i18n.th>
            <th className="w-6"></th>
          </tr>
        </thead>
        <tbody>
          {[...Array(3)].map((_, i) => (
            <tr key={`skeleton-${i}`}>
              <td className="w-1">
                <div className={clsx({ lp: true, small: true })}>
                  <div className="icon-denom skeleton h-2 w-2 br-2" />
                  <div className="icon-denom skeleton h-2 w-2 br-2" />
                </div>
              </td>
              <td>
                <div className="skeleton h-1 w-10 br-2" />
              </td>
              <td className="text-center">
                <div className="skeleton h-1 w-5 br-2 inline-block" />
              </td>
              <td className="text-center">
                <div className="skeleton h-1 w-5 br-2 inline-block" />
              </td>
              <td>
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-5 br-2 inline-block" />
              </td>
              <td className="text-right">
                <div className="skeleton h-1 w-10 br-2 inline-block" />
              </td>
              <td className="w-6">
                <div className="flex" style={{ gap: "0.5rem" }}>
                  <div className="skeleton h-2 w-2 br-2 inline-block" />
                  <div className="skeleton h-2 w-2 br-2 inline-block" />
                  <div className="skeleton h-2 w-2 br-2 inline-block" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const orderFragment = graphql`
  fragment LimitOrdersFragment on FinOrder {
    deviation
    filled
    filledValue
    filledFee
    offer
    owner
    pair {
      address
      assetBase {
        asset
        chain
        type
        metadata {
          symbol
          decimals
        }
      }
      assetQuote {
        asset
        chain
        type
        metadata {
          symbol
          decimals
        }
      }
      book {
        id
        center
      }
    }
    remaining
    remainingValue
    rate
    side
    type
    updatedAt
    offerValue
  }
`;

type LimitOrderItemType = FC<{
  k: LimitOrdersFragment$key;
  iconSize?: string;
}>;

const priceKey = (d: { deviation?: bigint | null; rate: bigint }) =>
  d.deviation
    ? { oracle: Number(d.deviation) }
    : { fixed: bigintToFixed(d.rate) };

export const LimitOrderItem: LimitOrderItemType = ({ k, iconSize }) => {
  const { showModal, hideModal } = useGlobalModalContext();
  const { selected } = useAccounts();
  const d = useFragment(orderFragment, k);
  const contract = d.pair.address;
  const { assetOffer, assetAsk } =
    d.side === "base"
      ? { assetOffer: d.pair.assetBase, assetAsk: d.pair.assetQuote }
      : { assetOffer: d.pair.assetQuote, assetAsk: d.pair.assetBase };

  const cancelMsg = useMemo(() => {
    if (!selected) return null;
    if (!contract) return null;

    return new MsgExecute(
      { address: selected.address, network: "THOR" },
      [],
      contract,
      {
        order: [[[d.side, priceKey(d), "0"]], null],
      }
    );
  }, [d, contract]);

  const claimMsg = useMemo(() => {
    if (!selected) return null;
    if (!contract) return null;

    return new MsgExecute(
      { address: selected.address, network: "THOR" },
      [],
      contract,
      {
        order: [[[d.side, priceKey(d), null]], null],
      }
    );
  }, [contract, d]);

  const confirmCancel = () => {
    showModal({
      backgroundClose: false,
      hideCloseButton: true,
      children: cancelMsg ? (
        <MsgProvider msg={cancelMsg}>
          <CancelLimitOrderModal hideModal={hideModal} />
        </MsgProvider>
      ) : null,
    });
  };

  const editOrder = (k: LimitOrdersFragment$key) => {
    showModal({
      title: "Edit Order",
      backgroundClose: false,
      children: (
        <BalanceProvider
          asset={d.side === "base" ? d.pair.assetBase : d.pair.assetQuote}>
          <EditLimitOrder k={k} hideModal={hideModal} />
        </BalanceProvider>
      ),
    });
  };

  return (
    <tr>
      <td>
        <Link
          to={`/trade/${toFinAssetId(d.pair.assetBase)}/${toFinAssetId(d.pair.assetQuote)}`}
          className={clsx({ lp: true, [`${iconSize}`]: iconSize })}>
          <IconDenom denom={d.pair.assetBase.metadata.symbol} className="w-2" />
          <IconDenom
            denom={d.pair.assetQuote.metadata.symbol}
            className="w-2"
          />
        </Link>
      </td>
      <td style={{ paddingLeft: "0" }}>
        <Link
          to={`/trade/${toFinAssetId(d.pair.assetBase)}/${toFinAssetId(d.pair.assetQuote)}`}
          className="color-white no-underline hover-teal fw-500 fs-14">
          {d.pair.assetBase.metadata.symbol}{" "}
          <span className="color-grey">/</span>{" "}
          {d.pair.assetQuote.metadata.symbol}
        </Link>
      </td>
      <td className="text-center">
        <LabelSide {...d} />
      </td>
      <td className="text-center">
        <LabelType {...d} />
      </td>
      <td className="mono fs-12">
        {format(d.updatedAt, "dd/MM/yy")}{" "}
        <span className="color-grey">{format(d.updatedAt, "HH:mm")}</span>
      </td>
      <td className="text-right mono">
        <Decimal
          subscript
          decimals={12}
          amount={BigInt(d.pair.book.center || 0)}
          symbol={d.pair.assetQuote.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
        />
      </td>
      <td className="text-right">
        <Price {...d} />
      </td>
      <td className="text-right">
        {d.side === "base" ? (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.offer)}
            symbol={assetOffer.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        ) : (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.offerValue)}
            symbol={assetAsk.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        )}
      </td>
      <td className="text-right">
        {d.side === "base" ? (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.remaining)}
            symbol={assetOffer.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        ) : (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.remainingValue)}
            symbol={assetAsk.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        )}
      </td>

      <td className="text-right">
        {d.side === "base" ? (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.offerValue)}
            symbol={assetAsk.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        ) : (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.offer)}
            symbol={assetOffer.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        )}
      </td>
      <td className="text-right color-teal">
        <StatusFilled {...d} />
      </td>
      <td className="text-right">
        {d.filled ? (
          <Decimal
            subscript
            round={4}
            amount={BigInt(d.filled)}
            symbol={assetAsk.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        ) : (
          <span className="color-grey">&mdash;</span>
        )}
      </td>
      <td className="text-right">
        <Decimal
          subscript
          round={4}
          amount={BigInt(d.filledFee)}
          symbol={assetAsk.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
        />
      </td>
      <td>
        <div className="flex">
          <button
            className="block transparent w-3 h-3 color-grey hover-white"
            onClick={() => editOrder(k)}
            data-tooltip-content="Edit order"
            data-tooltip-id="global-tip">
            <CirclePen className="block w-3 h-3" />
          </button>
          <button
            className={clsx({
              "block transparent w-3 h-3 color-grey": true,
              "opacity-10": Number(d.remaining) === 0,
              "hover-white": Number(d.remaining) > 0,
            })}
            data-tooltip-content="Cancel order"
            data-tooltip-id="global-tip"
            onClick={d.remaining ? confirmCancel : () => null}
            disabled={!d.remaining}>
            <CircleXMark className="block w-3 h-3" />
          </button>
          {Number(d.filled) > 0 ? (
            <MsgProvider msg={claimMsg}>
              <TxButton
                className="block transparent w-3 h-3"
                data-tooltip-content="Claim order"
                data-tooltip-id="global-tip"
                data-tooltip-float={false}
                hideSimulation>
                <ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
              </TxButton>
            </MsgProvider>
          ) : (
            <button
              className="block transparent w-3 h-3 color-grey opacity-10"
              data-tooltip-content="Claim order"
              data-tooltip-id="global-tip"
              data-tooltip-float={false}>
              <Claim className="block w-3 h-3" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const LabelSide: FC<LimitOrdersFragment$data> = ({ side }) => (
  <div
    className={clsx({
      "tag tag--borderless": true,
      "tag--red": side === "base",
      "tag--teal": side === "quote",
    })}>
    {side === "base" ? "Sell" : "Buy"}
  </div>
);

const LabelType: FC<LimitOrdersFragment$data> = ({ type }) => (
  <div
    className={clsx({
      "tag tag--borderless": true,
      "tag--grey": true,
    })}>
    {type === "fixed" ? "Limit" : "Oracle"}
  </div>
);

const StatusFilled: FC<LimitOrdersFragment$data> = ({ offer, remaining }) => {
  const filled = (Number(offer) - Number(remaining)) / Number(offer);
  return (
    <div
      className={clsx({
        "tag tag--borderless": true,
        "tag--red": filled < 0.25,
        "tag--orange": filled >= 0.25 && filled < 0.75,
        "tag--teal": filled >= 0.75,
      })}>
      {(filled * 100).toLocaleDecimal(2)}%
    </div>
  );
};

const Price: FC<LimitOrdersFragment$data> = ({ rate, deviation, pair }) => {
  switch (deviation) {
    case null:
    case undefined:
      return (
        <>
          <Decimal
            subscript
            decimals={12}
            amount={BigInt(rate)}
            symbol={pair.assetQuote.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
        </>
      );

    default:
      return (
        <div>
          <Decimal
            subscript
            decimals={12}
            amount={BigInt(rate)}
            symbol={pair.assetQuote.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          />
          <p className="inline-block color-grey condensed fs-12 fw-500 ml-1">
            ({Number(deviation) > 0 ? "+" : ""}
            {(Number(deviation) / 100).toLocaleDecimal(4)}%)
          </p>
        </div>
      );
  }
};

export const LimitOrdersHistory = () => {
  const isTouchDevice = useIsTouchDevice();
  const orders = [];
  return (
    <div className="trade__orders-table table-sticky table-sticky--left mt-2">
      {orders.length > 0 ? (
        <table
          className={clsx({
            "table nowrap table--spaced": true,
            "table--no-hover": isTouchDevice,
          })}>
          <thead>
            <tr>
              <i18n.th>Date</i18n.th>
              <i18n.th>Price</i18n.th>
              <i18n.th className="text-right">Amount</i18n.th>
              <i18n.th>Action</i18n.th>
            </tr>
          </thead>
        </table>
      ) : (
        <i18n.p className="condensed fs-14 color-grey text-center mt-2">
          Coming Soon...
          {/* There is no trade history for this pair */}
        </i18n.p>
      )}
    </div>
  );
};

const EditLimitOrder: FC<{
  k: LimitOrdersFragment$key;
  hideModal: () => void;
}> = ({ k, hideModal }) => {
  const d = useFragment(orderFragment, k);
  useTradeBookSubscription(d?.pair.book.id);
  const { selected } = useAccounts();

  const assetOffer = d.side === "base" ? d.pair.assetBase : d.pair.assetQuote;
  const assetAsk = d.side === "base" ? d.pair.assetQuote : d.pair.assetBase;
  const { balance } = usePreloadedBalance();
  const [amount, setAmount] = useState(BigInt(d.remaining));
  const funds = amount - BigInt(d.remaining);
  const coins =
    funds > 0n && balance?.asset.variants?.native?.denom
      ? [
          {
            denom: balance?.asset.variants?.native?.denom,
            amount: funds.toString(),
          },
        ]
      : [];

  const msg = useMemo(() => {
    if (amount === d.remaining) return null;
    if (!selected) return null;

    return new MsgExecute(
      { address: selected.address, network: "THOR" },
      coins,
      d.pair.address,
      {
        order: [[[d.side, priceKey(d), amount.toString()]], null],
      }
    );
  }, [d, amount]);

  return (
    <div className="flex dir-c gap-1">
      <div className="modal__header">
        <div className="flex ai-c w-full">
          <div className="lp big">
            <IconDenom denom={d.pair.assetBase.metadata.symbol} />
            <IconDenom denom={d.pair.assetQuote.metadata.symbol} />
          </div>
          <div className="ml-1 flex ai-c grow">
            <LabelSide {...d} />
            <div className="ml-0.5">
              <LabelType {...d} />
            </div>
            <div className="grow ml-6">
              <table className="table table--sm condensed color-grey">
                <tbody>
                  <tr>
                    <i18n.td>Order Price:</i18n.td>
                    <td className="text-right">
                      <Price {...d} />
                    </td>
                  </tr>
                  <tr>
                    <i18n.td>Current Price:</i18n.td>
                    <td className="text-right">
                      <div className="decimal color-white mt-0.5">
                        {priceFormatter(BigInt(d.pair.book.center || 0)) || "0"}
                        <span className="decimal__symbol decimal__symbol--small color-grey condensed fw-500">
                          {d.pair.assetQuote.metadata.symbol}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <InputQuantity amount={amount} setAmount={setAmount} />

      <div
        className="row pad--xs mt-2"
        style={{ marginLeft: "-0.5rem", marginRight: "-0.5rem" }}>
        <div className="col-6 flex">
          <div className="card px-2 py-1 flex fs-12 condensed color-grey grow">
            {i18n.t("Required")}:{" "}
            <div className="color-white ml-a">
              {nFormatter(funds > 0n ? funds : 0n, 6)}{" "}
              {assetOffer.metadata.symbol}
            </div>
          </div>
        </div>
        <div className="col-6 flex">
          <div className="card px-2 py-1 flex fs-12 condensed color-grey grow">
            {i18n.t("Withdrawn/Claimed")}:{" "}
            <div className="ml-a text-right">
              <div className="color-white mb-0.5">
                {nFormatter(funds < 0n ? -funds : 0n, 6)}{" "}
                {assetOffer.metadata.symbol}
              </div>
              {BigInt(d.filled) ? (
                <div className="color-white">
                  {nFormatter(BigInt(d.filled), 6)} {assetAsk.metadata.symbol}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={hideModal}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <MsgProvider msg={msg}>
            <TxButton
              className="button ml-1"
              label="Confirm"
              onSuccess={hideModal}
            />
          </MsgProvider>
        </div>
      </div>
    </div>
  );
};

const CancelLimitOrderModal: FC<{ hideModal: () => void }> = ({
  hideModal,
}) => {
  return (
    <>
      <div className="modal__header">
        <i18n.h2>Cancel Order</i18n.h2>
      </div>
      <i18n.p>Are you sure you want to cancel this order?</i18n.p>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={hideModal}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            className="button ml-1"
            label="Confirm"
            onSuccess={hideModal}
          />
        </div>
      </div>
    </>
  );
};
